import type { Invoice, Tariff, SimulationResult } from './types';

// Constantes para cálculo de impuestos españoles
const ELECTRICITY_TAX_RATE = 0.051127; // 5.1127% sobre subtotal energía+potencia
const VAT_RATE = 0.21; // 21% IVA

/**
 * Simula el coste de una tarifa usando los consumos de una factura real
 */
export function simulateTariff(invoice: Invoice, tariff: Tariff): SimulationResult | null {
  // Validar que tenemos datos mínimos necesarios
  if (!invoice.period.days || invoice.period.days <= 0) {
    console.warn('Invoice missing valid period days');
    return null;
  }

  const days = invoice.period.days;

  // Cálculo de potencia usando regla: "precio más caro cuando falta un período"
  const availablePowerPrices = [
    tariff.power.p1_eur_kw_day,
    tariff.power.p2_eur_kw_day,
    tariff.power.p3_eur_kw_day
  ].filter(price => price !== null && price !== undefined) as number[];
  
  const maxPowerPrice = availablePowerPrices.length > 0 ? Math.max(...availablePowerPrices) : 0;
  
  const power_p1 = calculatePowerCost(invoice.power.p1_kw, tariff.power.p1_eur_kw_day ?? maxPowerPrice, days);
  const power_p2 = calculatePowerCost(invoice.power.p2_kw, tariff.power.p2_eur_kw_day ?? maxPowerPrice, days);
  const power_p3 = calculatePowerCost(invoice.power.p3_kw, tariff.power.p3_eur_kw_day ?? maxPowerPrice, days);

  // Cálculo de energía usando regla: "precio más caro cuando falta un período"
  const isFlatRate = tariff.energy.p2_eur_kwh == null && tariff.energy.p3_eur_kwh == null;
  
  // Encontrar el precio más caro disponible para usar cuando falte un período
  const availablePrices = [
    tariff.energy.p1_eur_kwh,
    tariff.energy.p2_eur_kwh,
    tariff.energy.p3_eur_kwh
  ].filter(price => price !== null && price !== undefined) as number[];
  
  const maxPrice = availablePrices.length > 0 ? Math.max(...availablePrices) : 0;
  
  const energy_p1 = calculateEnergyCost(invoice.energy.p1_kwh, tariff.energy.p1_eur_kwh ?? maxPrice);
  
  const energy_p2 = isFlatRate 
    ? calculateEnergyCost(invoice.energy.p2_kwh, tariff.energy.p1_eur_kwh) // Tarifa fija: usar P1
    : calculateEnergyCost(invoice.energy.p2_kwh, tariff.energy.p2_eur_kwh ?? maxPrice); // Usar precio más caro si falta P2
  
  const energy_p3 = isFlatRate 
    ? calculateEnergyCost(invoice.energy.p3_kwh, tariff.energy.p1_eur_kwh) // Tarifa fija: usar P1
    : calculateEnergyCost(invoice.energy.p3_kwh, tariff.energy.p3_eur_kwh ?? maxPrice); // Usar precio más caro si falta P3

  // Extras (mantenemos los de la factura original)
  const extras_equipment = invoice.extras.equipment || 0;
  const extras_social_bonus = invoice.extras.social_bonus || 0;

  // Subtotal (base para impuesto eléctrico)
  const subtotal = power_p1 + power_p2 + power_p3 + energy_p1 + energy_p2 + energy_p3 + extras_equipment + extras_social_bonus;

  // Impuesto eléctrico (sobre subtotal de potencia + energía + extras)
  const electricity_tax = subtotal * ELECTRICITY_TAX_RATE;

  // Base para IVA (subtotal + impuesto eléctrico)
  const vat_base = subtotal + electricity_tax;

  // IVA (21% sobre base)
  const vat = vat_base * VAT_RATE;

  // Total final
  const total = vat_base + vat;

  // Comparación con factura original
  const invoice_total = invoice.total_pdf || 0;
  const difference = total - invoice_total;
  const percentage = invoice_total > 0 ? (difference / invoice_total) * 100 : 0;
  const is_cheaper = difference < 0;

  return {
    name: tariff.name,
    breakdown: {
      power_p1: roundToEuros(power_p1),
      power_p2: roundToEuros(power_p2),
      power_p3: roundToEuros(power_p3),
      energy_p1: roundToEuros(energy_p1),
      energy_p2: roundToEuros(energy_p2),
      energy_p3: roundToEuros(energy_p3),
      extras_equipment: roundToEuros(extras_equipment),
      extras_social_bonus: roundToEuros(extras_social_bonus),
      subtotal: roundToEuros(subtotal),
      electricity_tax: roundToEuros(electricity_tax),
      vat_base: roundToEuros(vat_base),
      vat: roundToEuros(vat),
      total: roundToEuros(total),
    },
    vs_invoice: {
      difference: roundToEuros(difference),
      percentage: roundToPercentage(percentage),
      is_cheaper,
    },
  };
}

/**
 * Calcula el coste de potencia: kW × €/kW·día × días
 */
function calculatePowerCost(kw: number | null, eur_kw_day: number | null, days: number): number {
  if (!kw || !eur_kw_day || kw <= 0 || eur_kw_day <= 0) {
    return 0;
  }
  return kw * eur_kw_day * days;
}

/**
 * Calcula el coste de energía: kWh × €/kWh
 */
function calculateEnergyCost(kwh: number | null, eur_kwh: number | null): number {
  if (!kwh || !eur_kwh || kwh <= 0 || eur_kwh <= 0) {
    return 0;
  }
  return kwh * eur_kwh;
}

/**
 * Redondea a 2 decimales para euros
 */
function roundToEuros(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Redondea a 1 decimal para porcentajes
 */
function roundToPercentage(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Formatea un número como precio en euros
 */
export function formatEuros(value: number | null): string {
  if (value === null || value === undefined) {
    return '—';
  }
  return `${value.toFixed(2)}€`;
}

/**
 * Formatea un porcentaje con signo
 */
export function formatPercentage(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

/**
 * Formatea diferencia con signo y color semántico
 */
export function formatDifference(value: number): { text: string; color: string } {
  if (value === 0) {
    return { text: '0€', color: 'text-gray-500' };
  }
  
  const sign = value > 0 ? '+' : '';
  const text = `${sign}${value.toFixed(2)}€`;
  const color = value < 0 ? 'text-green-600' : 'text-red-600';
  
  return { text, color };
}

/**
 * Calcula el total de una factura con/sin IVA
 */
export function getInvoiceTotal(invoice: Invoice, withVAT: boolean): number {
  if (withVAT) {
    return invoice.total_pdf || 0;
  }
  
  // Sin IVA = total - IVA
  const total_with_vat = invoice.total_pdf || 0;
  const vat = invoice.taxes.vat || 0;
  return total_with_vat - vat;
}

/**
 * Calcula el total de una simulación con/sin IVA
 */
export function getSimulationTotal(simulation: SimulationResult, withVAT: boolean): number {
  if (withVAT) {
    return simulation.breakdown.total;
  }
  
  // Sin IVA = total - IVA
  return simulation.breakdown.vat_base;
}
